import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import SupervisorEnterDashbord from './SupervisorEnterDashbord';
import SupervisorLogOut from './SupervisorLogOut';
import AddToQueueOutlinedIcon from '@mui/icons-material/AddToQueueOutlined';
import RemoveCircleOutlineOutlinedIcon from '@mui/icons-material/RemoveCircleOutlineOutlined';
import BusinessIcon from '@mui/icons-material/Business';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import DashboardIcon from '@mui/icons-material/Dashboard';
import DeleteIcon from '@mui/icons-material/Delete';
import { motion, AnimatePresence } from 'framer-motion';
import OperationsButton from './OperationsButton';

import {
  Tabs,
  Tab,
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Paper,
  Autocomplete,
  TextField,
  ListSubheader,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  Badge,
  Skeleton,
  Container,
  Card,
  CardContent,
  LinearProgress,
  useTheme,
  alpha,
  AppBar,
  Toolbar,
  Zoom,
  InputAdornment,
  Slide,
} from '@mui/material';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const safeParseMachines = () => {
  try {
    const raw = sessionStorage.getItem('machines');
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.warn('Invalid machines sessionStorage value. Clearing it.', error);
    sessionStorage.removeItem('machines');
    return [];
  }
};

const SupervisorMainPage = () => {
  const location = useLocation();
  const theme = useTheme();

  const supervisorId = location.state?.supervisorId || sessionStorage.getItem('supervisorId');
  const machinesFromState = location.state?.machines || safeParseMachines();

  const [machines, setMachines] = useState(machinesFromState);
  const [activeTab, setActiveTab] = useState(0);
  const [stationRemoveDialogOpen, setStationRemoveDialogOpen] = useState(false);
  const [selectedStationCode, setSelectedStationCode] = useState(null);

  const [groupedMachines, setGroupedMachines] = useState([]);
  const [selectedMachineCodes, setSelectedMachineCodes] = useState([]);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [loadingMachines, setLoadingMachines] = useState(false);
  const [refreshingMachines, setRefreshingMachines] = useState(false);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');

  const [searchTerm, setSearchTerm] = useState('');
  const [initialLoading, setInitialLoading] = useState(true);
  const [removeLoading, setRemoveLoading] = useState(false);

  const [stationRefreshKey, setStationRefreshKey] = useState(0);


  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbarOpen(false);
  };

  const triggerSnackbar = useCallback((message, severity = 'info') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  }, []);


const refreshSupervisorMachines = useCallback(
  async (showMessage = false) => {
    if (!supervisorId) return;

    try {
      setRefreshingMachines(true);

      const response = await axios.post(
        `${API_BASE_URL}/manhourGetLastUpdatedMachines`,
        { supervisorId }
      );

      let refreshedMachines = [];

      // Supports improved backend response:
      // { machines: [{ machineCode, groupCode, finishedGoodStatus }] }
      if (Array.isArray(response.data?.machines)) {
        refreshedMachines = response.data.machines
          .map((machine) => ({
            machineCode: machine.machineCode || machine.MachineCode,
            groupCode: machine.groupCode || machine.MachineGroupCode || "",
            finishedGoodStatus: Number(
              machine.finishedGoodStatus ?? machine.FinishedGood ?? 0
            ),
          }))
          .filter((machine) => machine.machineCode);
      } 
      // Supports current backend response:
      // { machineCodes: ["QCT01"], finishedGood: 0 }
      else {
        const machineCodes = response.data?.machineCodes || [];

        refreshedMachines = machineCodes.map((machineCode) => ({
          machineCode,
          groupCode: "",
          finishedGoodStatus: Number(response.data?.finishedGood || 0),
        }));
      }

      setMachines(refreshedMachines);

      sessionStorage.setItem("machines", JSON.stringify(refreshedMachines));
      sessionStorage.setItem("supervisorId", String(supervisorId));

      // This forces the currently opened SupervisorEnterDashbord tab
      // to refetch employee/job/operation data.
      setStationRefreshKey((prev) => prev + 1);

      // Keep activeTab valid after a workstation was removed by admin cleanup.
      setActiveTab((prev) => {
        if (refreshedMachines.length === 0) return 0;
        return prev >= refreshedMachines.length
          ? refreshedMachines.length - 1
          : prev;
      });

      if (showMessage) {
        triggerSnackbar("Dashboard synced with latest server data.", "success");
      }
    } catch (error) {
      console.error("Error refreshing supervisor machines:", error);

      if (showMessage) {
        triggerSnackbar("Failed to sync dashboard. Please try again.", "error");
      }
    } finally {
      setRefreshingMachines(false);
    }
  },
  [supervisorId, triggerSnackbar]
);


  const AppleDialogHeader = ({ title, icon: Icon, onClose, color = 'linear-gradient(135deg, #007aff 0%, #5856d6 100%)' }) => (
    <DialogTitle sx={{
      p: 0,
      background: color,
      position: 'relative',
      overflow: 'hidden',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0.05) 100%)',
        pointerEvents: 'none',
      }
    }}>
      <Box sx={{
        height: '32px',
        background: 'rgba(255, 255, 255, 0.1)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        pl: 2,
        pr: 2,
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
      }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Box sx={{
            width: 12,
            height: 12,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #ff5f57 0%, #ff3b30 100%)',
            cursor: 'pointer',
            '&:hover': { transform: 'scale(1.1)' },
            transition: 'transform 0.2s ease',
          }} onClick={onClose} />
          <Box sx={{
            width: 12,
            height: 12,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #ffbd2e 0%, #ff9500 100%)',
            transition: 'transform 0.2s ease',
          }} />
          <Box sx={{
            width: 12,
            height: 12,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #28cd41 0%, #30d158 100%)',
            transition: 'transform 0.2s ease',
          }} />
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            color: 'rgba(255, 255, 255, 0.8)',
            '&:hover': { color: 'white', backgroundColor: 'rgba(255, 255, 255, 0.1)' }
          }}
          size="small"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ p: 3, pb: 4, position: 'relative', zIndex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
          <Box sx={{
            width: 56,
            height: 56,
            borderRadius: '16px',
            background: 'rgba(255, 255, 255, 0.2)',
            backdropFilter: 'blur(10px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            mb: 2,
          }}>
            <Icon sx={{
              fontSize: 28,
              color: 'white',
              filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1))',
            }} />
          </Box>
        </Box>

        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: 'white',
            textAlign: 'center',
            fontSize: '28px',
            letterSpacing: '-0.5px',
            lineHeight: 1.2,
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
          }}
        >
          {title}
        </Typography>
      </Box>
    </DialogTitle>
  );

  const AppleButton = ({ variant = 'contained', color = 'primary', children, onClick, disabled, startIcon, sx, ...props }) => {
    const getButtonStyles = () => {
      if (variant === 'contained') {
        switch (color) {
          case 'primary':
            return {
              background: 'linear-gradient(135deg, #007aff 0%, #5856d6 100%)',
              '&:hover': { background: 'linear-gradient(135deg, #0056cc 0%, #4c44cc 100%)' }
            };
          case 'success':
            return {
              background: 'linear-gradient(135deg, #34c759 0%, #30d158 100%)',
              '&:hover': { background: 'linear-gradient(135deg, #2a9d47 0%, #28a745 100%)' }
            };
          case 'error':
            return {
              background: 'linear-gradient(135deg, #ff3b30 0%, #ff5f57 100%)',
              '&:hover': { background: 'linear-gradient(135deg, #d70015 0%, #e53935 100%)' }
            };
          case 'warning':
            return {
              background: 'linear-gradient(135deg, #ff9500 0%, #ffbd2e 100%)',
              '&:hover': { background: 'linear-gradient(135deg, #e6840e 0%, #f4a20c 100%)' }
            };
          default:
            return {
              background: 'linear-gradient(135deg, #007aff 0%, #5856d6 100%)',
              '&:hover': { background: 'linear-gradient(135deg, #0056cc 0%, #4c44cc 100%)' }
            };
        }
      }

      return {
        borderColor: 'transparent',
        backgroundColor: 'rgba(142, 142, 147, 0.12)',
        color: '#007aff',
        border: 'none',
        '&:hover': {
          backgroundColor: 'rgba(142, 142, 147, 0.2)',
          borderColor: 'transparent',
        },
      };
    };

    return (
      <Button
        variant={variant}
        onClick={onClick}
        disabled={disabled}
        startIcon={startIcon}
        sx={{
          borderRadius: '12px',
          px: 4,
          py: 1.5,
          fontSize: '16px',
          fontWeight: 600,
          color: variant === 'contained' ? 'white' : undefined,
          border: 'none',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          boxShadow: variant === 'contained' ? '0 4px 16px rgba(0, 122, 255, 0.3)' : 'none',
          '&:hover': {
            boxShadow: variant === 'contained' ? '0 6px 20px rgba(0, 122, 255, 0.4)' : 'none',
            transform: 'translateY(-2px) scale(1.02)',
          },
          '&:disabled': {
            background: 'rgba(142, 142, 147, 0.3)',
            color: 'rgba(255, 255, 255, 0.6)',
          },
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          ...getButtonStyles(),
          ...sx,
        }}
        {...props}
      >
        {children}
      </Button>
    );
  };

  useEffect(() => {
    sessionStorage.setItem('supervisorId', supervisorId || '');
    sessionStorage.setItem('machines', JSON.stringify(machines));
  }, [supervisorId, machines]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setInitialLoading(false);
    }, 700);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (supervisorId) {
      refreshSupervisorMachines(false);
    }
  }, [supervisorId, refreshSupervisorMachines]);

  useEffect(() => {
    const handlePopState = () => {
      window.history.pushState(null, document.title, window.location.href);
      triggerSnackbar('Navigation restricted - Please use the logout button to exit', 'warning');
    };

    window.addEventListener('popstate', handlePopState);
    window.history.pushState(null, document.title, window.location.href);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [triggerSnackbar]);



  useEffect(() => {
  const handleVisibilityChange = () => {
    if (!document.hidden && supervisorId) {
      refreshSupervisorMachines(false);
    }
  };

  window.addEventListener("focus", handleVisibilityChange);
  document.addEventListener("visibilitychange", handleVisibilityChange);

  return () => {
    window.removeEventListener("focus", handleVisibilityChange);
    document.removeEventListener("visibilitychange", handleVisibilityChange);
  };
}, [supervisorId, refreshSupervisorMachines]);



  const fetchAvailableMachines = async () => {
    try {
      setLoadingMachines(true);
      triggerSnackbar('Loading available workstations...', 'info');

      const groupsRes = await axios.get(`${API_BASE_URL}/MachineGroups/${supervisorId}`);
      const groups = groupsRes.data || [];

      const machinePromises = groups.map((groupCode) =>
        axios.get(`${API_BASE_URL}/ManhourMachineNames?groupCode=${groupCode}`)
      );

      const machineResponses = await Promise.all(machinePromises);

      const grouped = [];

      groups.forEach((groupCode, index) => {
        const groupMachines = machineResponses[index].data || [];

        if (groupMachines.length > 0) {
          grouped.push({
            isGroupHeader: true,
            groupName: groupCode,
          });

          groupMachines.forEach((machine) => {
            const isAlreadySelected = machines.some(
              (existingMachine) => existingMachine.machineCode === machine.MachineCode
            );

            if (!isAlreadySelected) {
              grouped.push({
                ...machine,
                isGroupHeader: false,
                groupCode,
              });
            }
          });
        }
      });

      setGroupedMachines(grouped);
      setAddDialogOpen(true);
    } catch (err) {
      console.error('Error fetching available machines:', err);
      triggerSnackbar('Error loading available workstations. Please try again.', 'error');
    } finally {
      setLoadingMachines(false);
    }
  };

  const handleConfirmAddMachines = async () => {
    if (selectedMachineCodes.length === 0) {
      triggerSnackbar('Please select at least one workstation to add.', 'warning');
      return;
    }

    try {
      const selectedMachinesData = groupedMachines.filter(
        (machine) => !machine.isGroupHeader && selectedMachineCodes.includes(machine.MachineCode)
      );

      const formattedNewMachines = selectedMachinesData.map((machine) => ({
        machineCode: machine.MachineCode,
        groupCode: machine.groupCode,
        finishedGoodStatus: Number(machine.FinishedGood || 0),
      }));

      const response = await axios.post(
        `${API_BASE_URL}/manhourSupervisorSelectStation`,
        {
          supervisorId,
          machineCodes: selectedMachineCodes,
        }
      );

      if (response.status === 200) {
        const nextMachines = [...machines];

        formattedNewMachines.forEach((newMachine) => {
          const exists = nextMachines.some((m) => m.machineCode === newMachine.machineCode);
          if (!exists) nextMachines.push(newMachine);
        });

        setMachines(nextMachines);
        sessionStorage.setItem('machines', JSON.stringify(nextMachines));

        triggerSnackbar(`🎉 ${selectedMachineCodes.length} workstation(s) added successfully!`, 'success');

        setSelectedMachineCodes([]);
        setAddDialogOpen(false);
        setGroupedMachines([]);
      } else {
        triggerSnackbar('Failed to add workstations. Please try again.', 'error');
      }
    } catch (error) {
      console.error('Error adding workstations:', error);
      triggerSnackbar('An error occurred while adding workstations.', 'error');
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleRemoveClick = async (machineCode) => {
    try {
      setRemoveLoading(true);

      const response = await axios.post(
        `${API_BASE_URL}/manhourCheckActiveEmployees`,
        { stationCode: machineCode, supervisorId }
      );

      if (response.status === 200 && response.data.data.length === 0) {
        setSelectedStationCode(machineCode);
        setStationRemoveDialogOpen(true);
      } else {
        triggerSnackbar('There are active employees logged in on this station.', 'warning');
      }
    } catch (error) {
      console.error('Error checking active employees:', error);
      triggerSnackbar('An error occurred while checking active employees.', 'error');
    } finally {
      setRemoveLoading(false);
    }
  };

  const handleConfirmRemove = async () => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/manhourUpdateMachineStatus`,
        { stationCode: selectedStationCode }
      );

      if (response.status === 200) {
        const updatedStationCode = response.data.stationCode;
        const updatedMachines = machines.filter(
          (machine) => machine.machineCode !== updatedStationCode
        );

        setMachines(updatedMachines);
        sessionStorage.setItem('machines', JSON.stringify(updatedMachines));
        setActiveTab((prev) => {
          if (updatedMachines.length === 0) return 0;
          return prev >= updatedMachines.length ? updatedMachines.length - 1 : prev;
        });

        triggerSnackbar('✅ Machine status updated and station removed successfully.', 'success');
        await refreshSupervisorMachines(false);
      } else {
        triggerSnackbar('Failed to update machine status.', 'error');
      }
    } catch (error) {
      console.error('Error updating machine status:', error);
      triggerSnackbar('An error occurred while updating machine status.', 'error');
    } finally {
      setStationRemoveDialogOpen(false);
      setSelectedStationCode(null);
    }
  };

  const filteredMachines = machines.filter((machine) =>
    machine.machineCode.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (initialLoading) {
    return (
      <Box sx={{ width: '100%', height: '100vh' }}>
        <AppBar position="static" elevation={0} sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
        }}>
          <Toolbar sx={{ minHeight: '80px' }}>
            <Skeleton variant="rectangular" width={200} height={40} sx={{ borderRadius: 2 }} />
            <Box sx={{ ml: 2, flex: 1 }}>
              <Skeleton variant="rectangular" width="50%" height={30} sx={{ borderRadius: 2 }} />
            </Box>
          </Toolbar>
        </AppBar>

        <Container maxWidth="xl" sx={{ py: 4 }}>
          <Card elevation={3} sx={{ borderRadius: 4 }}>
            <CardContent>
              <LinearProgress sx={{ mb: 2, borderRadius: 2 }} />
              <Skeleton variant="rectangular" width="100%" height={400} sx={{ borderRadius: 2 }} />
            </CardContent>
          </Card>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      <AppBar position="static" elevation={0} sx={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        borderRadius: 0,
      }}>
        <Toolbar sx={{ minHeight: '80px', px: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
            <DashboardIcon sx={{ fontSize: 32, color: 'white' }} />
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: 'white', lineHeight: 1 }}>
                Supervisor Dashboard
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                ID: {supervisorId} • {machines.length} Active Stations
              </Typography>
            </Box>
          </Box>

          {machines.length > 3 && (
            <Box sx={{ mx: 3 }}>
              <TextField
                size="small"
                placeholder="Search stations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ color: 'rgba(255,255,255,0.7)', mr: 1 }} />,
                }}
                sx={{
                  minWidth: 250,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 25,
                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                    color: 'white',
                    '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.5)' },
                    '&.Mui-focused fieldset': { borderColor: 'white' },
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: 'rgba(255,255,255,0.7)',
                  }
                }}
              />
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <Tooltip title="Refresh workstation list from database">
              <Button
                variant="contained"
                color="info"
                sx={{
                  borderRadius: 25,
                  px: 3,
                  py: 1,
                  textTransform: 'none',
                  fontWeight: 600,
                  background: 'linear-gradient(45deg, #03a9f4 30%, #29b6f6 90%)',
                  boxShadow: '0 4px 15px rgba(3, 169, 244, 0.3)',
                  '&:hover': {
                    boxShadow: '0 6px 20px rgba(3, 169, 244, 0.4)',
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.3s ease',
                }}
                onClick={() => refreshSupervisorMachines(true)}
                disabled={refreshingMachines}
                startIcon={refreshingMachines ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
              >
                {refreshingMachines ? 'Refreshing...' : 'Sync Dashboard'}
              </Button>
            </Tooltip>

            <Tooltip title="Add new workstation to monitor">
              <Button
                variant="contained"
                color="success"
                sx={{
                  borderRadius: 25,
                  px: 3,
                  py: 1,
                  textTransform: 'none',
                  fontWeight: 600,
                  background: 'linear-gradient(45deg, #4caf50 30%, #66bb6a 90%)',
                  boxShadow: '0 4px 15px rgba(76, 175, 80, 0.3)',
                  '&:hover': {
                    boxShadow: '0 6px 20px rgba(76, 175, 80, 0.4)',
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.3s ease',
                }}
                onClick={fetchAvailableMachines}
                disabled={loadingMachines}
                startIcon={loadingMachines ? <CircularProgress size={20} color="inherit" /> : <AddToQueueOutlinedIcon />}
              >
                {loadingMachines ? 'Loading...' : 'Add Station'}
              </Button>
            </Tooltip>

            {activeTab !== null && machines[activeTab] && (
              <Tooltip title="Remove current workstation">
                <Button
                  variant="contained"
                  color="error"
                  startIcon={removeLoading ? <CircularProgress size={20} color="inherit" /> : <RemoveCircleOutlineOutlinedIcon />}
                  onClick={() => handleRemoveClick(machines[activeTab].machineCode)}
                  disabled={removeLoading}
                  sx={{
                    borderRadius: 25,
                    px: 3,
                    py: 1,
                    textTransform: 'none',
                    fontWeight: 600,
                    boxShadow: '0 4px 15px rgba(244, 67, 54, 0.3)',
                    '&:hover': {
                      boxShadow: '0 6px 20px rgba(244, 67, 54, 0.4)',
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  {removeLoading ? 'Checking...' : 'Remove'}
                </Button>
              </Tooltip>
            )}

            {activeTab !== null && machines[activeTab] && (
              <OperationsButton
                machineCode={machines[activeTab].machineCode}
                groupCode={machines[activeTab].groupCode}
                supervisorId={supervisorId}
                onTriggerSnackbar={triggerSnackbar}
              />
            )}

            {machines.length === 0 && <SupervisorLogOut username={supervisorId} />}
          </Box>
        </Toolbar>
      </AppBar>

      {machines && machines.length > 0 && (
        <Box sx={{
          backgroundColor: 'white',
          borderBottom: '1px solid #e0e0e0',
          px: 3,
          py: 1
        }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            aria-label="machine code tabs"
            sx={{
              '& .MuiTab-root': {
                borderRadius: '20px 20px 0 0',
                mx: 0.5,
                minHeight: 48,
                transition: 'all 0.3s ease',
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                },
                '&.Mui-selected': {
                  backgroundColor: theme.palette.primary.main,
                  color: 'white',
                  boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
                }
              }
            }}
          >
            {filteredMachines.map((machine) => {
              const realIndex = machines.findIndex((m) => m.machineCode === machine.machineCode);

              return (
                <Tab
                  key={machine.machineCode}
                  value={realIndex}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 'inherit' }}>
                        {machine.machineCode}
                      </Typography>
                      <Badge
                        badgeContent={machine.finishedGoodStatus}
                        color={machine.finishedGoodStatus > 0 ? 'success' : 'default'}
                        variant="dot"
                      />
                    </Box>
                  }
                />
              );
            })}
          </Tabs>
        </Box>
      )}

      <Container maxWidth="xl" sx={{ py: 3 }}>
        {machines && machines.length > 0 ? (
          <AnimatePresence mode="wait">
            {machines.map((machine, index) => (
              activeTab === index && (
                <motion.div
                  key={machine.machineCode}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                  style={{ width: '100%' }}
                >
                  <Card
                    elevation={4}
                    sx={{
                      borderRadius: 4,
                      background: 'rgba(255, 255, 255, 0.95)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255, 255, 255, 0.2)'
                    }}
                  >
                    <CardContent sx={{ p: 0 }}>
                      <SupervisorEnterDashbord
                        supervisorId={supervisorId}
                        machineCode={machine.machineCode}
                        Groupcode={machine.groupCode}
                        finishedGood={machine.finishedGoodStatus}
                        refreshKey={stationRefreshKey}
                        onTriggerSnackbar={triggerSnackbar}
                      />
                    </CardContent>
                  </Card>
                </motion.div>
              )
            ))}
          </AnimatePresence>
        ) : (
          <Card
            elevation={3}
            sx={{
              borderRadius: 4,
              background: 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)'
            }}
          >
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  py: 8,
                  textAlign: 'center'
                }}
              >
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  <InfoIcon sx={{ fontSize: 64, color: theme.palette.info.main, mb: 2 }} />
                  <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                    No Workstations Selected
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                    Get started by adding workstations to monitor
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                    <AppleButton
                      startIcon={<RefreshIcon />}
                      onClick={() => refreshSupervisorMachines(true)}
                      disabled={refreshingMachines}
                    >
                      Refresh Workstations
                    </AppleButton>
                    <AppleButton
                      startIcon={<AddToQueueOutlinedIcon />}
                      onClick={fetchAvailableMachines}
                      disabled={loadingMachines}
                      color="success"
                    >
                      Add Workstation
                    </AppleButton>
                  </Box>
                </motion.div>
              </Box>
            </CardContent>
          </Card>
        )}
      </Container>

      <Dialog
        open={stationRemoveDialogOpen}
        onClose={() => setStationRemoveDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '24px',
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(40px) saturate(180%)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            boxShadow: '0 32px 64px rgba(0, 0, 0, 0.12), 0 8px 16px rgba(0, 0, 0, 0.08)',
            overflow: 'hidden',
          }
        }}
        BackdropProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.4)',
            backdropFilter: 'blur(8px)',
          }
        }}
        TransitionComponent={Zoom}
        transitionDuration={400}
      >
        <AppleDialogHeader
          title="Remove Workstation"
          icon={WarningIcon}
          color="linear-gradient(135deg, #ff3b30 0%, #ff5f57 100%)"
          onClose={() => setStationRemoveDialogOpen(false)}
        />

        <DialogContent sx={{
          p: 4,
          background: 'rgba(248, 249, 250, 0.8)',
          backdropFilter: 'blur(20px)',
        }}>
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: '#1d1d1f',
                mb: 3,
                fontSize: '20px',
                letterSpacing: '-0.2px',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              }}
            >
              Are you sure you want to remove this workstation?
            </Typography>

            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: '16px',
                background: 'linear-gradient(135deg, rgba(255, 59, 48, 0.08) 0%, rgba(255, 95, 87, 0.08) 100%)',
                border: '2px solid rgba(255, 59, 48, 0.15)',
                backdropFilter: 'blur(20px)',
                mb: 3,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Box sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, #ff3b30 0%, #ff5f57 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 4px 12px rgba(255, 59, 48, 0.3)',
                }}>
                  <DeleteIcon sx={{ color: 'white', fontSize: 20 }} />
                </Box>
                <Box sx={{ textAlign: 'left' }}>
                  <Typography variant="body1" sx={{ fontWeight: 700, color: '#1d1d1f', fontSize: '16px' }}>
                    Workstation: {selectedStationCode}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#8e8e93', fontSize: '14px', fontWeight: 500 }}>
                    This action cannot be undone
                  </Typography>
                </Box>
              </Box>
            </Paper>

            <Typography
              variant="body1"
              sx={{
                color: '#8e8e93',
                fontSize: '16px',
                fontWeight: 500,
                lineHeight: 1.5,
                mb: 2,
              }}
            >
              The workstation will be removed from this supervisor dashboard and the machine will be released.
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{
          p: 4,
          gap: 3,
          background: 'rgba(248, 249, 250, 0.8)',
          backdropFilter: 'blur(20px)',
          borderTop: '1px solid rgba(0, 0, 0, 0.06)',
        }}>
          <AppleButton
            variant="outlined"
            onClick={() => setStationRemoveDialogOpen(false)}
            sx={{ minWidth: '120px' }}
          >
            Cancel
          </AppleButton>
          <AppleButton
            color="error"
            onClick={handleConfirmRemove}
            startIcon={<DeleteIcon />}
            sx={{ minWidth: '160px' }}
          >
            Remove Station
          </AppleButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={addDialogOpen}
        onClose={() => {
          setAddDialogOpen(false);
          setSelectedMachineCodes([]);
          setGroupedMachines([]);
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '24px',
            background: 'rgba(255, 255, 255, 0.90)',
            backdropFilter: 'blur(40px) saturate(180%)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            boxShadow: '0 32px 64px rgba(0, 0, 0, 0.12), 0 8px 16px rgba(0, 0, 0, 0.08)',
            overflow: 'hidden',
            transform: 'translateZ(0)',
          }
        }}
        BackdropProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.4)',
            backdropFilter: 'blur(8px)',
          }
        }}
        TransitionComponent={Zoom}
        transitionDuration={400}
      >
        <AppleDialogHeader
          title="Add Workstations"
          icon={AddToQueueOutlinedIcon}
          onClose={() => {
            setAddDialogOpen(false);
            setSelectedMachineCodes([]);
            setGroupedMachines([]);
          }}
        />

        <DialogContent sx={{
          p: 4,
          background: 'rgba(248, 249, 250, 0.8)',
          backdropFilter: 'blur(20px)',
        }}>
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: '#1d1d1f',
                mb: 3,
                fontSize: '20px',
                letterSpacing: '-0.2px',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              }}
            >
              Choose Workstations
            </Typography>

            <Autocomplete
              multiple
              options={groupedMachines}
              value={groupedMachines.filter((m) => !m.isGroupHeader && selectedMachineCodes.includes(m.MachineCode))}
              onChange={(event, newValue) => {
                const validSelections = newValue.filter((item) => !item.isGroupHeader);
                setSelectedMachineCodes(validSelections.map((item) => item.MachineCode));
              }}
              getOptionLabel={(option) =>
                option.isGroupHeader
                  ? option.groupName
                  : `${option.MachineDescription} - ${option.MachineCode}`
              }
              getOptionDisabled={(option) => option.isGroupHeader}
              isOptionEqualToValue={(option, value) =>
                !option.isGroupHeader && !value.isGroupHeader &&
                option.MachineCode === value.MachineCode
              }
              renderOption={(props, option, { selected }) => {
                if (option.isGroupHeader) {
                  return (
                    <ListSubheader
                      key={option.groupName}
                      sx={{
                        fontWeight: 700,
                        fontSize: '15px',
                        color: '#007aff',
                        background: 'linear-gradient(135deg, rgba(0, 122, 255, 0.08) 0%, rgba(88, 86, 214, 0.08) 100%)',
                        lineHeight: '52px',
                        borderRadius: '12px',
                        margin: '8px 12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(0, 122, 255, 0.15)',
                        letterSpacing: '-0.2px',
                        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                      }}
                    >
                      <BusinessIcon sx={{ fontSize: 20, ml: 2 }} />
                      {option.groupName}
                    </ListSubheader>
                  );
                }

                return (
                  <li
                    {...props}
                    key={option.MachineCode}
                    style={{
                      padding: '16px 20px',
                      borderRadius: '12px',
                      margin: '4px 12px',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      backgroundColor: selected ? 'rgba(0, 122, 255, 0.1)' : 'transparent',
                      border: selected ? '2px solid rgba(0, 122, 255, 0.3)' : '2px solid transparent',
                    }}
                  >
                    <Box sx={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      border: `2px solid ${selected ? '#007aff' : '#d1d1d6'}`,
                      backgroundColor: selected ? '#007aff' : 'transparent',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 3,
                      transition: 'all 0.3s ease',
                      flexShrink: 0,
                    }}>
                      {selected && (
                        <Box sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: 'white',
                        }} />
                      )}
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          mb: 1,
                          color: '#1d1d1f',
                          fontSize: '16px',
                          letterSpacing: '-0.1px',
                          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                        }}
                      >
                        {option.MachineDescription}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <Chip
                          label={option.MachineCode}
                          size="small"
                          sx={{
                            fontSize: '12px',
                            fontWeight: 600,
                            height: '24px',
                            backgroundColor: 'rgba(0, 122, 255, 0.1)',
                            color: '#007aff',
                            border: '1px solid rgba(0, 122, 255, 0.2)',
                          }}
                        />
                        <Typography variant="caption" sx={{ color: '#8e8e93', fontSize: '13px', fontWeight: 500 }}>
                          Group: {option.groupCode}
                        </Typography>
                      </Box>
                    </Box>
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search workstations..."
                  variant="outlined"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: '#8e8e93', fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '16px',
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      backdropFilter: 'blur(20px)',
                      border: '2px solid rgba(0, 0, 0, 0.04)',
                      fontSize: '16px',
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '& fieldset': { border: 'none' },
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 25px rgba(0, 0, 0, 0.08)',
                      },
                      '&.Mui-focused': {
                        backgroundColor: 'rgba(255, 255, 255, 1)',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 25px rgba(0, 122, 255, 0.15)',
                        border: '2px solid rgba(0, 122, 255, 0.3)',
                      },
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '16px',
                      fontWeight: 500,
                      '&::placeholder': {
                        color: '#8e8e93',
                        opacity: 1,
                      },
                    },
                  }}
                />
              )}
              ListboxProps={{
                sx: {
                  maxHeight: 400,
                  borderRadius: '20px',
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(40px)',
                  border: '1px solid rgba(0, 0, 0, 0.08)',
                  boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
                  '& .MuiAutocomplete-option': {
                    borderRadius: '12px',
                    margin: '4px 12px',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 122, 255, 0.08)',
                      transform: 'translateX(4px)',
                    },
                    '&.Mui-focused': {
                      backgroundColor: 'rgba(0, 122, 255, 0.12)',
                      transform: 'translateX(4px)',
                    },
                  },
                }
              }}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    key={option.MachineCode}
                    variant="filled"
                    label={option.MachineCode}
                    {...getTagProps({ index })}
                    sx={{
                      height: '32px',
                      borderRadius: '8px',
                      background: 'linear-gradient(135deg, #007aff 0%, #5856d6 100%)',
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '13px',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      backdropFilter: 'blur(10px)',
                      '& .MuiChip-deleteIcon': {
                        color: 'rgba(255, 255, 255, 0.8)',
                        fontSize: '18px',
                        '&:hover': {
                          color: 'white',
                          backgroundColor: 'rgba(255, 255, 255, 0.2)',
                        },
                      },
                      '&:hover': { transform: 'scale(1.05)' },
                      transition: 'all 0.2s ease',
                    }}
                  />
                ))
              }
            />
          </Box>

          <AnimatePresence>
            {selectedMachineCodes.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -20, scale: 0.95 }}
                transition={{ duration: 0.4, type: 'spring', stiffness: 300, damping: 25 }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    borderRadius: '20px',
                    background: 'linear-gradient(135deg, rgba(52, 199, 89, 0.1) 0%, rgba(48, 209, 88, 0.1) 100%)',
                    border: '2px solid rgba(52, 199, 89, 0.2)',
                    backdropFilter: 'blur(20px)',
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                >
                  <Box sx={{ position: 'relative', zIndex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                      <Box sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '12px',
                        background: 'linear-gradient(135deg, #34c759 0%, #30d158 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 4px 12px rgba(52, 199, 89, 0.3)',
                      }}>
                        <CheckCircleIcon sx={{ color: 'white', fontSize: 22 }} />
                      </Box>
                      <Box>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 700,
                            color: '#1d1d1f',
                            fontSize: '18px',
                            letterSpacing: '-0.2px',
                            lineHeight: 1.2,
                            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                          }}
                        >
                          {selectedMachineCodes.length} Workstation{selectedMachineCodes.length !== 1 ? 's' : ''} Selected
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#8e8e93', fontSize: '14px', fontWeight: 500 }}>
                          Ready to add to your management scope
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                      gap: 2,
                    }}>
                      {selectedMachineCodes.map((code, index) => (
                        <motion.div
                          key={code}
                          initial={{ opacity: 0, scale: 0.8, y: 20 }}
                          animate={{ opacity: 1, scale: 1, y: 0 }}
                          transition={{ duration: 0.3, delay: index * 0.05, type: 'spring', stiffness: 400, damping: 25 }}
                        >
                          <Chip
                            label={code}
                            sx={{
                              height: '36px',
                              width: '100%',
                              borderRadius: '12px',
                              background: 'linear-gradient(135deg, #34c759 0%, #30d158 100%)',
                              color: 'white',
                              fontWeight: 600,
                              fontSize: '14px',
                              border: '1px solid rgba(255, 255, 255, 0.3)',
                              backdropFilter: 'blur(10px)',
                              boxShadow: '0 4px 12px rgba(52, 199, 89, 0.25)',
                              '&:hover': {
                                transform: 'translateY(-2px) scale(1.05)',
                                boxShadow: '0 6px 16px rgba(52, 199, 89, 0.35)',
                              },
                              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            }}
                          />
                        </motion.div>
                      ))}
                    </Box>
                  </Box>
                </Paper>
              </motion.div>
            )}
          </AnimatePresence>
        </DialogContent>

        <DialogActions sx={{
          p: 4,
          gap: 3,
          background: 'rgba(248, 249, 250, 0.8)',
          backdropFilter: 'blur(20px)',
          borderTop: '1px solid rgba(0, 0, 0, 0.06)',
        }}>
          <AppleButton
            variant="outlined"
            onClick={() => {
              setAddDialogOpen(false);
              setSelectedMachineCodes([]);
              setGroupedMachines([]);
            }}
            sx={{ minWidth: '120px' }}
          >
            Cancel
          </AppleButton>
          <AppleButton
            onClick={handleConfirmAddMachines}
            disabled={selectedMachineCodes.length === 0}
            sx={{ minWidth: '200px' }}
          >
            Add {selectedMachineCodes.length > 0 && `(${selectedMachineCodes.length})`} Workstation{selectedMachineCodes.length !== 1 ? 's' : ''}
          </AppleButton>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={snackbarSeverity === 'success' ? 4000 : 6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        TransitionComponent={Slide}
        TransitionProps={{ direction: 'up' }}
      >
        <Alert
          variant="filled"
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{
            borderRadius: '16px',
            fontWeight: 600,
            fontSize: '15px',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)',
            '& .MuiAlert-icon': { fontSize: 22 },
            '& .MuiAlert-action': {
              '& .MuiIconButton-root': {
                color: 'rgba(255, 255, 255, 0.8)',
                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
              },
            },
            ...(snackbarSeverity === 'success' && {
              background: 'linear-gradient(135deg, #34c759 0%, #30d158 100%)',
            }),
            ...(snackbarSeverity === 'error' && {
              background: 'linear-gradient(135deg, #ff3b30 0%, #ff5f57 100%)',
            }),
            ...(snackbarSeverity === 'warning' && {
              background: 'linear-gradient(135deg, #ff9500 0%, #ffbd2e 100%)',
            }),
            ...(snackbarSeverity === 'info' && {
              background: 'linear-gradient(135deg, #007aff 0%, #5856d6 100%)',
            }),
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SupervisorMainPage;
